import { useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Button, Drawer, Form, FormInstance, Input, Select, notification } from 'antd';
import styled from 'styled-components';

import * as CmsPageService from '../../../services/bb-api/cms/page';
import type { PageCreate } from '../../../services/bb-api/cms/page';
import { txt } from '../../../locales/i18n';

export const CreatePageDrawer = (props: { visible: boolean; onClose: () => void }) => {
	const [isCreating, setIsCreating] = useState(false);
	const history = useHistory();
	const formRef = useRef<FormInstance>(null);

	const createPage = async (page: PageCreate) => {
		const form = formRef.current;
		if (form === null) return;

		const [urlError = null] = form.getFieldError('url');
		if (urlError !== null) return notification.error({ message: urlError });

		setIsCreating(true);

		try {
			const pageId = await CmsPageService.create(page);
			history.push('/cms/' + pageId);
			notification.success({ message: txt('notification.create successful') });
		} catch (error) {
			if (error instanceof Error && error.message === 'url already exists') {
				const errorMessage = txt('cms.notifications.urlExists');
				form.setFields([{ name: 'url', errors: [errorMessage] }]);
			}

			notification.error({ message: txt('notification.create failed') });
		}

		setIsCreating(false);
	};

	const handleValuesChange = (values: any) => {
		const form = formRef.current;
		if (form === null) return;

		const [[key, value]] = Object.entries(values);

		if (key === 'title') {
			form.setFields([{ name: key, errors: [] }]);

			const maxTitleLength = 60;
			const titleLength = (value as string).length;
			if (titleLength < maxTitleLength) return;

			const errorMessage = txt('cms.notifications.titleLength', {
				maxTitleLength,
			});

			form.setFields([{ name: key, errors: [errorMessage] }]);
		}

		if (key === 'metaDescription') {
			form.setFields([{ name: key, errors: [] }]);

			const maxMetaDescriptionLength = 160;
			const metaDescriptionLength = (value as string).length;
			if (metaDescriptionLength < maxMetaDescriptionLength) return;

			const errorMessage = txt('cms.notifications.metaDescriptionLength', {
				maxMetaDescriptionLength,
			});

			form.setFields([{ name: key, errors: [errorMessage] }]);
		}

		if (key === 'url') {
			form.setFields([{ name: key, errors: [] }]);
			const urlPattern = /^\w[/\w-]*$/gi;
			if (urlPattern.test(value as string)) return;

			const errorMessage = txt('cms.notifications.urlFormat');
			form.setFields([{ name: key, errors: [errorMessage] }]);
		}
	};

	return (
		<DrawerStyled
			title={txt('cms.createPage')}
			visible={props.visible}
			onClose={props.onClose}
			placement="left"
			width={420}
		>
			<Form
				ref={formRef}
				onFinish={createPage}
				onValuesChange={handleValuesChange}
				initialValues={{ keywords: [], region: 'dk' }}
			>
				<label>{txt('cms.title')}</label>
				<Form.Item name="title">
					<Input.TextArea autoSize showCount required />
				</Form.Item>

				<label>{txt('cms.url')}</label>
				<Form.Item name="url">
					<Input required />
				</Form.Item>

				<label>{txt('cms.metaDescription')}</label>
				<Form.Item name="metaDescription">
					<Input.TextArea autoSize showCount required />
				</Form.Item>

				<label>{txt('cms.keywords')}</label>
				<Form.Item name="keywords">
					<Select mode="tags" style={{ width: '100%' }} />
				</Form.Item>

				<label>{txt('cms.region')}</label>
				<Form.Item name="region" required>
					<Select style={{ width: '100%' }}>
						<Select.Option value="dk">Dk</Select.Option>
						<Select.Option value="se">Se</Select.Option>
						<Select.Option value="de">De</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item>
					<Button type="primary" htmlType="submit" loading={isCreating}>
						{txt('cms.createPage')}
					</Button>
				</Form.Item>
			</Form>
		</DrawerStyled>
	);
};

const DrawerStyled = styled(Drawer)``;
